import type { FileDetail, FileKind, LookupItem } from '@jmc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiRequestError } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { LocationPicker, LocationStatus } from '../components/LocationPicker.js';
import {
  cx,
  ErrorState,
  Field,
  InlineError,
  LoadingBlock,
  Mono,
  PageHeader,
  Panel,
  Select,
  Spinner,
  TextInput,
} from '../components/ui.js';

/**
 * Manual entry and correction of a single file.
 *
 * The field order deliberately mirrors the printed ISO forms, so a clerk with
 * the physical sheet in front of them works top to bottom without hunting. The
 * property block appears only for Form 2, exactly as on paper.
 *
 * Bulk import handles the 1 lakh backlog; this handles the file that arrives
 * tomorrow, and the spelling that needs correcting today.
 */

interface FormState {
  kind: FileKind;
  fileNumber: string;
  fileName: string;
  subjectEn: string;
  subjectHi: string;
  departmentId: string;
  zoneId: string;
  wardNo: string;
  oicName: string;
  financialYear: string;
  schemeNo: string;
  schemeNameEn: string;
  schemeNameHi: string;
  societyName: string;
  sector: string;
  plotNo: string;
  houseNo: string;
  firstAllotteeEn: string;
  firstAllotteeHi: string;
  leaseAcNo: string;
  leaseDescription: string;
  remark: string;
  roomCode: string;
  rackCode: string;
  columnCode: string;
  bagCode: string;
}

const EMPTY: FormState = {
  kind: 'PROPERTY',
  fileNumber: '',
  fileName: '',
  subjectEn: '',
  subjectHi: '',
  departmentId: '',
  zoneId: '',
  wardNo: '',
  oicName: '',
  financialYear: '',
  schemeNo: '',
  schemeNameEn: '',
  schemeNameHi: '',
  societyName: '',
  sector: '',
  plotNo: '',
  houseNo: '',
  firstAllotteeEn: '',
  firstAllotteeHi: '',
  leaseAcNo: '',
  leaseDescription: '',
  remark: '',
  roomCode: '',
  rackCode: '',
  columnCode: '',
  bagCode: '',
};

function fromDetail(file: FileDetail): FormState {
  return {
    kind: file.kind,
    fileNumber: file.fileNumber ?? '',
    fileName: file.fileName ?? '',
    subjectEn: file.subjectEn ?? '',
    subjectHi: file.subjectHi ?? '',
    departmentId: file.departmentId ? String(file.departmentId) : '',
    zoneId: file.zoneId ? String(file.zoneId) : '',
    wardNo: file.wardNo ?? '',
    oicName: file.oicName ?? '',
    financialYear: file.financialYear ?? '',
    schemeNo: file.schemeNo ?? '',
    schemeNameEn: file.schemeNameEn ?? '',
    schemeNameHi: file.schemeNameHi ?? '',
    societyName: file.societyName ?? '',
    sector: file.sector ?? '',
    plotNo: file.plotNo ?? '',
    houseNo: file.houseNo ?? '',
    firstAllotteeEn: file.firstAllotteeEn ?? '',
    firstAllotteeHi: file.firstAllotteeHi ?? '',
    leaseAcNo: file.leaseAcNo ?? '',
    leaseDescription: file.leaseDescription ?? '',
    remark: file.remark ?? '',
    roomCode: file.location?.roomCode ?? '',
    rackCode: file.location?.rackCode ?? '',
    columnCode: file.location?.columnCode ?? '',
    bagCode: file.location?.bagCode ?? '',
  };
}

/** Blank strings become null so the API stores "not recorded", not "". */
function toPayload(form: FormState) {
  const text = (value: string) => (value.trim() === '' ? null : value.trim());
  const isProperty = form.kind === 'PROPERTY';

  return {
    kind: form.kind,
    fileNumber: text(form.fileNumber),
    fileName: text(form.fileName),
    subjectEn: text(form.subjectEn),
    subjectHi: text(form.subjectHi),
    departmentId: form.departmentId ? Number(form.departmentId) : null,
    zoneId: form.zoneId ? Number(form.zoneId) : null,
    wardNo: text(form.wardNo),
    oicName: text(form.oicName),
    financialYear: text(form.financialYear),
    // A general file carries no property block; clearing it here stops a
    // half-filled Form 2 leaving orphan values behind after a kind change.
    schemeNo: isProperty ? text(form.schemeNo) : null,
    schemeNameEn: isProperty ? text(form.schemeNameEn) : null,
    schemeNameHi: isProperty ? text(form.schemeNameHi) : null,
    societyName: isProperty ? text(form.societyName) : null,
    sector: isProperty ? text(form.sector) : null,
    plotNo: isProperty ? text(form.plotNo) : null,
    houseNo: isProperty ? text(form.houseNo) : null,
    firstAllotteeEn: isProperty ? text(form.firstAllotteeEn) : null,
    firstAllotteeHi: isProperty ? text(form.firstAllotteeHi) : null,
    leaseAcNo: isProperty ? text(form.leaseAcNo) : null,
    leaseDescription: isProperty ? text(form.leaseDescription) : null,
    remark: text(form.remark),
    location: {
      roomCode: form.roomCode.trim(),
      rackCode: form.rackCode.trim(),
      columnCode: form.columnCode.trim(),
      bagCode: form.bagCode.trim(),
    },
  };
}

export function FileFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { t, pick } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const existing = useQuery({
    queryKey: ['file-edit', id],
    queryFn: () => api<FileDetail>(`/files/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing.data) setForm(fromDetail(existing.data));
  }, [existing.data]);

  const lookups = useQuery({
    queryKey: ['lookups'],
    queryFn: () => api<{ departments: LookupItem[]; zones: LookupItem[] }>('/files/lookups'),
    staleTime: 10 * 60_000,
  });

  /**
   * Look for an existing record that matches what is being typed.
   *
   * The importer refuses a repeated file number outright. Manual entry used to
   * accept it silently, so the same file could be keyed twice and the two
   * paths disagreed about the same rule. The warning is deliberately not a
   * block: the clerk is holding the physical file and can see whether it is
   * the same one -- a hard refusal would just teach them to invent a
   * different file number.
   */
  const duplicates = useQuery({
    queryKey: ['dupes', form.fileNumber, form.leaseAcNo, form.plotNo, form.firstAllotteeEn, id],
    queryFn: () => {
      const search = new URLSearchParams();
      if (form.fileNumber.trim()) search.set('fileNumber', form.fileNumber.trim());
      if (form.leaseAcNo.trim()) search.set('leaseAcNo', form.leaseAcNo.trim());
      if (form.plotNo.trim()) search.set('plotNo', form.plotNo.trim());
      if (form.firstAllotteeEn.trim()) search.set('firstAllotteeEn', form.firstAllotteeEn.trim());
      if (id) search.set('excludeId', id);
      return api<{ matches: Array<{ id: number; eFileNumber: string; reason: string }> }>(
        `/files/possible-duplicates?${search.toString()}`,
      );
    },
    enabled: Boolean(
      form.fileNumber.trim() ||
        form.leaseAcNo.trim() ||
        (form.plotNo.trim() && form.firstAllotteeEn.trim()),
    ),
    staleTime: 15_000,
  });

  const [addAnother, setAddAnother] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      const payload = toPayload(form);
      return isEdit
        ? api<FileDetail>(`/files/${id}`, { method: 'PATCH', body: payload })
        : api<FileDetail>('/files', { method: 'POST', body: payload });
    },
    onSuccess: (file) => {
      void queryClient.invalidateQueries({ queryKey: ['files'] });
      void queryClient.invalidateQueries({ queryKey: ['file', String(file.id)] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['rooms'] });
      void queryClient.invalidateQueries({ queryKey: ['location-codes'] });

      if (addAnother && !isEdit) {
        // Keep the shelf and the classification, clear the file's identity:
        // a clerk working through one bag enters twenty files from the same
        // almirah, and retyping the location each time is where typos start.
        setForm((current) => ({
          ...EMPTY,
          kind: current.kind,
          departmentId: current.departmentId,
          zoneId: current.zoneId,
          wardNo: current.wardNo,
          oicName: current.oicName,
          financialYear: current.financialYear,
          roomCode: current.roomCode,
          rackCode: current.rackCode,
          columnCode: current.columnCode,
          bagCode: current.bagCode,
        }));
        setSaved(file.eFileNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      navigate(`/files/${file.id}`, {
        replace: true,
        state: { toast: isEdit ? t('file.updated') : t('file.created') },
      });
    },
    onError: (caught) => {
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        setFields(caught.fields);
      } else {
        setError(t('common.error'));
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  const set = (key: keyof FormState) => (value: string) => {
    if (saved) setSaved(null);
    setForm((current) => ({ ...current, [key]: value }));
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setFields({});
    save.mutate();
  };

  if (isEdit && existing.isLoading) return <LoadingBlock />;
  if (isEdit && existing.isError) {
    return <ErrorState error={existing.error} onRetry={() => void existing.refetch()} />;
  }

  const isProperty = form.kind === 'PROPERTY';
  const locationFilled =
    form.roomCode.trim() && form.rackCode.trim() && form.columnCode.trim() && form.bagCode.trim();

  return (
    <div className="mx-auto max-w-4xl">
      <button type="button" onClick={() => navigate(-1)} className="btn-ghost mb-3 -ml-2">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="m11.5 5-5 5 5 5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {t('action.back')}
      </button>

      <PageHeader
        title={isEdit ? t('file.edit') : t('file.new')}
        subtitle={isEdit ? existing.data?.eFileNumber : undefined}
      />

      {saved && (
        <div className="mb-4 rounded-md border border-green-line bg-green-bg px-4 py-2.5 text-green-ink">
          {t('file.created')} — <Mono className="font-medium">{saved}</Mono>
        </div>
      )}

      {error && (
        <div className="mb-4">
          <InlineError message={error} />
        </div>
      )}

      {(duplicates.data?.matches.length ?? 0) > 0 && (
        <div className="mb-4 rounded-md border border-amber-line bg-amber-bg px-4 py-3">
          <p className="font-medium text-amber-ink">{t('file.possibleDuplicate')}</p>
          <p className="mt-0.5 text-[0.8125rem] text-ink-700">{t('file.duplicateHint')}</p>
          <ul className="mt-2 space-y-1">
            {duplicates.data!.matches.map((match) => (
              <li key={match.id}>
                <Link
                  to={`/files/${match.id}`}
                  target="_blank"
                  className="text-navy-600 hover:underline"
                >
                  {match.eFileNumber}
                </Link>
                <span className="ml-2 text-[0.8125rem] text-ink-500">
                  {t(`file.reason.${match.reason}` as never)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Section title={t('file.identity')}>
          <div className="sm:col-span-2">
            <Field label={t('file.formType')} hint={t('file.kindHint')}>
              <div className="flex flex-wrap gap-2">
                {(['GENERAL', 'PROPERTY'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => set('kind')(option)}
                    aria-pressed={form.kind === option}
                    className={cx(
                      'rounded-[5px] border px-3 py-1.5 transition-colors',
                      form.kind === option
                        ? 'border-navy-600 bg-navy-50 font-medium text-navy-700'
                        : 'border-line-strong text-ink-700 hover:border-navy-300',
                    )}
                  >
                    {option === 'GENERAL' ? `Form 1 — ${t('kind.GENERAL')}` : `Form 2 — ${t('kind.PROPERTY')}`}
                  </button>
                ))}
              </div>
            </Field>
          </div>

          <Field label={t('field.fileNumber')} error={fields['fileNumber']}>
            <TextInput
              value={form.fileNumber}
              onChange={(event) => set('fileNumber')(event.target.value)}
              placeholder="F6/(14621)/JMC/DC(REV2)/C-13/18-19"
            />
          </Field>

          <Field label={t('field.fileName')} error={fields['fileName']}>
            <TextInput
              value={form.fileName}
              onChange={(event) => set('fileName')(event.target.value)}
            />
          </Field>

          <Field label={t('field.subject')} error={fields['subjectEn']}>
            <TextInput
              value={form.subjectEn}
              onChange={(event) => set('subjectEn')(event.target.value)}
            />
          </Field>

          <Field label={t('field.subjectHi')} error={fields['subjectHi']}>
            <TextInput
              lang="hi"
              value={form.subjectHi}
              onChange={(event) => set('subjectHi')(event.target.value)}
              placeholder="सी-13 बनीपार्क योजना"
            />
          </Field>

          <Field label={t('field.year')} error={fields['financialYear']}>
            <TextInput
              value={form.financialYear}
              onChange={(event) => set('financialYear')(event.target.value)}
              placeholder="2018-2019"
            />
          </Field>

          <Field label={t('field.oicName')} error={fields['oicName']}>
            <TextInput
              value={form.oicName}
              onChange={(event) => set('oicName')(event.target.value)}
              placeholder="DC (REV2)"
            />
          </Field>
        </Section>

        <Section title={t('file.classification')}>
          <Field label={t('field.department')} error={fields['departmentId']}>
            <Select
              value={form.departmentId}
              onChange={(event) => set('departmentId')(event.target.value)}
            >
              <option value="">{t('common.none')}</option>
              {lookups.data?.departments.map((item) => (
                <option key={item.id} value={item.id}>
                  {pick(item.nameEn, item.nameHi)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('field.zone')} error={fields['zoneId']}>
            <Select value={form.zoneId} onChange={(event) => set('zoneId')(event.target.value)}>
              <option value="">{t('common.none')}</option>
              {lookups.data?.zones.map((item) => (
                <option key={item.id} value={item.id}>
                  {pick(item.nameEn, item.nameHi)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('field.ward')} error={fields['wardNo']}>
            <TextInput
              inputMode="numeric"
              value={form.wardNo}
              onChange={(event) => set('wardNo')(event.target.value)}
              placeholder="32"
            />
          </Field>
        </Section>

        {isProperty && (
          <Section title={t('file.property')}>
            <Field label={t('field.scheme')} error={fields['schemeNameEn']}>
              <TextInput
                value={form.schemeNameEn}
                onChange={(event) => set('schemeNameEn')(event.target.value)}
                placeholder="Bani Park Yojna"
              />
            </Field>

            <Field label={t('field.schemeHi')} error={fields['schemeNameHi']}>
              <TextInput
                lang="hi"
                value={form.schemeNameHi}
                onChange={(event) => set('schemeNameHi')(event.target.value)}
                placeholder="बनीपार्क योजना"
              />
            </Field>

            <Field label={t('field.schemeNo')} error={fields['schemeNo']}>
              <TextInput
                value={form.schemeNo}
                onChange={(event) => set('schemeNo')(event.target.value)}
                placeholder="1068"
              />
            </Field>

            <Field label={t('field.society')} error={fields['societyName']}>
              <TextInput
                value={form.societyName}
                onChange={(event) => set('societyName')(event.target.value)}
              />
            </Field>

            <Field label={t('field.sector')} error={fields['sector']}>
              <TextInput value={form.sector} onChange={(event) => set('sector')(event.target.value)} />
            </Field>

            <Field label={t('field.plotNo')} error={fields['plotNo']}>
              <TextInput
                value={form.plotNo}
                onChange={(event) => set('plotNo')(event.target.value)}
                placeholder="C-13"
              />
            </Field>

            <Field label={t('field.houseNo')} error={fields['houseNo']}>
              <TextInput
                value={form.houseNo}
                onChange={(event) => set('houseNo')(event.target.value)}
              />
            </Field>

            <Field label={t('field.leaseAcNo')} error={fields['leaseAcNo']}>
              <TextInput
                inputMode="numeric"
                value={form.leaseAcNo}
                onChange={(event) => set('leaseAcNo')(event.target.value)}
                placeholder="610843783"
              />
            </Field>

            <Field label={t('field.allottee')} error={fields['firstAllotteeEn']}>
              <TextInput
                value={form.firstAllotteeEn}
                onChange={(event) => set('firstAllotteeEn')(event.target.value)}
                placeholder="Ram Lal"
              />
            </Field>

            <Field label={t('field.allotteeHi')} error={fields['firstAllotteeHi']}>
              <TextInput
                lang="hi"
                value={form.firstAllotteeHi}
                onChange={(event) => set('firstAllotteeHi')(event.target.value)}
                placeholder="राम लाल"
              />
            </Field>

            <div className="sm:col-span-2">
              <Field label={t('field.leaseDescription')} error={fields['leaseDescription']}>
                <TextInput
                  value={form.leaseDescription}
                  onChange={(event) => set('leaseDescription')(event.target.value)}
                  placeholder="H.No.: C-13, Bani Park Yojna, Major Shiv Dayal Singh"
                />
              </Field>
            </div>
          </Section>
        )}

        <Section title={t('file.locationSection')} hint={t('file.locationHint')}>
          <LocationPicker
            value={{
              roomCode: form.roomCode,
              rackCode: form.rackCode,
              columnCode: form.columnCode,
              bagCode: form.bagCode,
            }}
            onChange={(next) => setForm((current) => ({ ...current, ...next }))}
            errors={fields}
          />
          <div className="sm:col-span-2">
            <LocationStatus
              value={{
                roomCode: form.roomCode,
                rackCode: form.rackCode,
                columnCode: form.columnCode,
                bagCode: form.bagCode,
              }}
            />
          </div>
        </Section>

        <Section title={t('file.notes')}>
          <div className="sm:col-span-2">
            <Field label={t('field.remark')} error={fields['remark']}>
              <TextInput
                value={form.remark}
                onChange={(event) => set('remark')(event.target.value)}
              />
            </Field>
          </div>
        </Section>

        <div className="flex flex-wrap justify-end gap-2 pb-6">
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            {t('action.cancel')}
          </button>
          {!isEdit && (
            <button
              type="submit"
              className="btn-secondary"
              disabled={save.isPending || !locationFilled}
              onClick={() => setAddAnother(true)}
            >
              {t('file.saveAndNext')}
            </button>
          )}
          <button
            type="submit"
            className="btn-primary"
            disabled={save.isPending || !locationFilled}
            onClick={() => setAddAnother(false)}
          >
            {save.isPending && <Spinner />}
            {save.isPending ? t('action.saving') : t('action.save')}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <Panel
      title={
        <span>
          {title}
          {hint && <span className="ml-2 font-normal text-ink-500">{hint}</span>}
        </span>
      }
      bodyClassName="grid gap-4 px-5 py-5 sm:grid-cols-2"
    >
      {children}
    </Panel>
  );
}
